import type { SimplePlace } from "$lib/features/place/place.type";
import { PAVED, RoadSurface, UNPAVED_LABELS } from "$lib/features/route/routeStep/roadSurface.enum";
import type { TourStop } from "$lib/features/tour/stop/tourStop.type";

export interface RankedStop {
    name: string;
    index: number;
}
export const META_TITLE_MAX = 60;
export const META_DESCRIPTION_MAX = 160;

// Returns the first candidate within the limit, or the last (shortest) one if none fit.
export function firstThatFits(candidates: string[], max: number): string {
    return candidates.find((c) => c.length <= max) ?? candidates[candidates.length - 1];
}

/**
 * @param breakdown surface -> distance (or share). Pass whatever feeds your
 * "Surface breakdown" section; only the proportions matter.
 */
export function buildSurfacePhrase(breakdown: Partial<Record<RoadSurface, number>>): string | null {
    let all = 0;
    let known = 0;
    let paved = 0;
    let topUnpaved: { surface: RoadSurface; amount: number } | null = null;

    for (const [key, amount] of Object.entries(breakdown) as [RoadSurface, number][]) {
        if (!amount || amount <= 0) continue;
        all += amount;
        if (key === RoadSurface.UNKNOWN || key === RoadSurface.IMPASSABLE) continue;
        known += amount;
        if (PAVED.has(key)) paved += amount;
        else if (!topUnpaved || amount > topUnpaved.amount) topUnpaved = { surface: key, amount };
    }

    // Don't claim anything if most of the route is unmapped
    if (all === 0 || known / all < 0.7) return null;

    const pavedPct = Math.floor((paved / known) * 100);
    if (pavedPct >= 50) return pavedPct === 100 ? 'fully paved' : `${pavedPct}% paved`;

    if (topUnpaved && topUnpaved.amount / known >= 0.5) {
        return `mostly ${UNPAVED_LABELS[topUnpaved.surface] ?? 'unpaved'}`;
    }
    return 'mostly unpaved';
}

/** Stops ordered best-first. Slice the top N, then sort by `index` to display in route order. */
export function rankStops(stops: TourStop[]): RankedStop[] {
    return stops
        .map((stop, index) => ({ name: stop.englishName, index, score: scorePlace(stop.place) }))
        .sort((a, b) => b.score - a.score || a.index - b.index)
        .map(({ name, index }) => ({ name, index }));
}

function scorePlace(place: SimplePlace): number {
    let score = 0;
    if (place.unescoId) score += 5;
    if (place.heritageDesignations?.length) score += 3;
    if (place.visitorsPerYear) score += 2;
    if (place.architects?.length || place.architecturalStyles?.length) score += 1;
    if (place.inceptionYear) score += 1;
    if (place.wikidataId) score += 1;
    return score;
}